import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Phone, PhoneCall, Globe, ShieldCheck, ArrowRight, Signal, Calculator,
  DollarSign, MapPin, Headphones, Clock, Users, Wallet, Wifi, CreditCard, Hash, Smartphone, QrCode, Plane, Menu, X, MessageCircle, Eye, EyeOff, Loader2,
} from "lucide-react";
import { SupportChat } from "@/components/SupportChat";
import { FeedbackCampaign } from "@/components/FeedbackCampaign";
import { HowToSlideshow } from "@/components/HowToSlideshow";

const HERO_MAN =
  "/images/hero-image-2.png";
const HERO_DIVERSE = "/images/hero-diverse.jpg";
const HERO_CC = [
  ["🇺🇸", "US", "+1"], ["🇬🇧", "UK", "+44"], ["🇺🇬", "UG", "+256"],
  ["🇰🇪", "KE", "+254"], ["🇳🇬", "NG", "+234"], ["🇬🇭", "GH", "+233"],
  ["🇿🇦", "ZA", "+27"], ["🇹🇿", "TZ", "+255"], ["🇷🇼", "RW", "+250"],
  ["🇿🇲", "ZM", "+260"], ["🇿🇼", "ZW", "+263"], ["🇪🇹", "ET", "+251"],
  ["🇸🇴", "SO", "+252"], ["🇨🇩", "CD", "+243"], ["🇨🇲", "CM", "+237"],
  ["🇸🇳", "SN", "+221"], ["🇨🇮", "CI", "+225"], ["🇩🇪", "DE", "+49"],
  ["🇫🇷", "FR", "+33"], ["🇳🇱", "NL", "+31"], ["🇧🇪", "BE", "+32"],
  ["🇸🇪", "SE", "+46"], ["🇳🇴", "NO", "+47"], ["🇩🇰", "DK", "+45"],
  ["🇮🇹", "IT", "+39"], ["🇪🇸", "ES", "+34"], ["🇮🇪", "IE", "+353"],
  ["🇦🇺", "AU", "+61"], ["🇦🇪", "AE", "+971"], ["🇸🇦", "SA", "+966"],
  ["🇶🇦", "QA", "+974"], ["🇮🇳", "IN", "+91"],
];
const WORKER_IMG =
  "/images/hero-image-3.png";
const RURAL_IMG =
  "/images/hero-woman-calling.png";

const NAV_LINKS = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Travel eSIMs", href: "#esim" },
  { label: "Rates", href: "#features" },
  { label: "Features", href: "#features" },
  { label: "About Us", href: "#about" },
  { label: "Help", href: "#about" },
];

export default function Landing() {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [heroEmail, setHeroEmail] = React.useState("");
  const [heroCc, setHeroCc] = React.useState("+1");
  const [heroPhone, setHeroPhone] = React.useState("");
  const [heroStep, setHeroStep] = React.useState(1);
  const [heroName, setHeroName] = React.useState("");
  const [heroPassword, setHeroPassword] = React.useState("");
  const [heroShowPw, setHeroShowPw] = React.useState(false);
  const [heroError, setHeroError] = React.useState("");
  const [heroLoading, setHeroLoading] = React.useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const heroSubmit = async (e) => {
    e.preventDefault();
    setHeroError("");
    const digits = heroPhone.replace(/\D/g, "");
    if (heroStep === 1) {
      if (!heroEmail.includes("@")) return setHeroError("Add your email address");
      setHeroStep(2);
      return;
    }
    if (!heroName.trim()) return setHeroError("Enter your full name");
    if (heroPassword.length < 6) return setHeroError("Password must be at least 6 characters");
    if (digits.length > 0 && digits.length < 6) return setHeroError("That phone number looks too short — or leave it empty to skip");
    setHeroLoading(true);
    try {
      const u = await register(heroName.trim(), heroEmail, heroPassword, digits.length >= 6 ? `${heroCc}${digits}` : "");
      toast.success(u?.balance > 0 ? `Welcome! 🎁 $${u.balance.toFixed(2)} free credit added — try your first call.` : "Account created!");
      navigate("/app");
    } catch (err) {
      const d = err.response?.data?.detail;
      setHeroError(typeof d === "string" ? d : "Could not create your account — please try again.");
    } finally {
      setHeroLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2">
          <a href="#top" className="flex flex-col shrink-0" data-testid="landing-brand-logo">
            <img src="/branding/zanitel_logo.png" alt="Zeeptel" className="h-8 sm:h-9 w-auto" />
            <span className="hidden sm:block text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-400 mt-0.5 pl-0.5">
              connecting the world
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-600">
            {NAV_LINKS.map((l) => (
              <a key={l.label} href={l.href} className="hover:text-[#2563EB] transition-colors">
                {l.label}
              </a>
            ))}
            <span className="inline-flex items-center gap-1.5 text-slate-500">
              <Globe className="h-4 w-4" /> EN
            </span>
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <Link
              to="/login"
              data-testid="landing-login-link"
              className="px-3 sm:px-5 py-2 rounded-xl text-sm font-semibold border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors whitespace-nowrap"
            >
              Log In
            </Link>
            <Link
              to="/register"
              data-testid="landing-signup-link"
              className="px-3 sm:px-5 py-2 rounded-xl text-sm font-semibold bg-[#2563EB] text-white hover:bg-[#1D4ED8] shadow-md shadow-blue-500/25 transition-all hover:-translate-y-0.5 whitespace-nowrap"
            >
              Get Started
            </Link>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="landing-mobile-menu-btn"
              aria-label="Menu"
              className="lg:hidden p-2 rounded-xl border border-slate-200 text-slate-600"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav className="lg:hidden border-t border-slate-100 bg-white px-4 py-3 space-y-1" data-testid="landing-mobile-menu">
            {NAV_LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                className="block px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                {l.label}
              </a>
            ))}
          </nav>
        )}
      </header>

      <section id="top" className="px-3 sm:px-5 lg:px-8 pt-3 pb-4 bg-white">
        <div className="relative max-w-[1440px] mx-auto rounded-[1.75rem] lg:rounded-[2.25rem] overflow-hidden shadow-2xl" data-testid="landing-hero">
          <img
            src={HERO_DIVERSE}
            alt="Friends from around the world laughing together on phone calls"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/20 to-black/50" />

          <div className="relative z-10 grid lg:grid-cols-2 gap-10 lg:gap-6 items-center px-5 sm:px-10 lg:px-16 py-12 sm:py-16 lg:py-24 min-h-[540px] lg:min-h-[660px]">
            {/* Left: big spacious headline */}
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 max-w-xl">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white drop-shadow-lg">
                Call home,
                <br />
                anywhere.
              </h1>
              <p className="mt-5 text-base md:text-lg text-white/90 max-w-md drop-shadow">
                Simple, affordable calls to any phone on earth — no internet needed on their side. Works even on 2G and landlines, in 185+ countries.
              </p>
              <p className="mt-3 text-xs sm:text-sm text-white/70">
                <span data-testid="hero-tagline-connecting" className="font-bold uppercase tracking-wide">Connecting the world</span>
                {" · "}
                <span data-testid="hero-tagline-bridge">Let&rsquo;s bridge the communication</span>
              </p>

              <div className="mt-7 flex flex-wrap gap-2.5">
                <button
                  onClick={() => window.dispatchEvent(new Event("zani-open"))}
                  data-testid="hero-talk-zani"
                  className="ai-glow-btn inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs sm:text-sm font-bold shadow-lg hover:-translate-y-0.5 transition-all"
                >
                  <MessageCircle className="h-4 w-4" /> Talk to our Zeep assistant
                </button>
                <a
                  href="#esim"
                  data-testid="hero-buy-esim"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#E11D48] text-white text-xs sm:text-sm font-bold shadow-lg shadow-red-500/30 hover:bg-[#BE123C] hover:-translate-y-0.5 transition-all uppercase tracking-wide"
                >
                  <Smartphone className="h-4 w-4" /> Buy e-SIM cards
                </a>
              </div>

              {/* Chat bubble accents */}
              <div className="hidden md:flex flex-col items-start gap-2 mt-10">
                <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-2 shadow-xl text-sm font-medium text-slate-800">
                  Mama, can you hear me? <span className="text-slate-400 text-[10px] ml-1.5">11:53</span>
                </div>
                <div className="bg-[#DCFCE7] rounded-2xl rounded-bl-sm px-4 py-2 shadow-xl text-sm font-medium text-slate-800 ml-6">
                  Clear as day, my dear! <span className="text-slate-400 text-[10px] ml-1.5">11:53 ✓✓</span>
                </div>
              </div>
            </div>

            {/* Right: glass create-account card */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:justify-self-end animate-in fade-in slide-in-from-bottom-6 duration-700">
              <div
                className="backdrop-blur-xl bg-black/40 border border-white/20 rounded-3xl px-6 sm:px-9 py-8 sm:py-10 text-center shadow-2xl"
                data-testid="hero-create-account-card"
              >
                <h2 className="text-white text-2xl sm:text-3xl font-extrabold font-heading">Create an account</h2>
                <p className="text-white/90 font-semibold mt-2 text-sm sm:text-base">Call from your browser — no number needed</p>
                <form onSubmit={heroSubmit} className="mt-6 space-y-3 text-left">
                  <div>
                    <label className="block text-xs font-bold text-white/90 mb-1.5 ml-1">Email</label>
                    <input
                      type="email"
                      value={heroEmail}
                      onChange={(e) => setHeroEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="hero-email-input"
                      className="w-full h-12 rounded-full bg-white text-slate-900 placeholder:text-slate-400 px-6 text-sm font-medium outline-none focus:ring-2 focus:ring-[#E11D48]/60"
                    />
                  </div>
                  {heroStep === 2 && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="hero-step2-fields">
                      <div>
                        <label className="block text-xs font-bold text-white/90 mb-1.5 ml-1">Full name</label>
                        <input
                          type="text"
                          value={heroName}
                          onChange={(e) => setHeroName(e.target.value)}
                          placeholder="Ada Obi"
                          data-testid="hero-name-input"
                          className="w-full h-12 rounded-full bg-white text-slate-900 placeholder:text-slate-400 px-6 text-sm font-medium outline-none focus:ring-2 focus:ring-[#E11D48]/60"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-white/90 mb-1.5 ml-1">Password</label>
                        <div className="relative">
                          <input
                            type={heroShowPw ? "text" : "password"}
                            value={heroPassword}
                            onChange={(e) => setHeroPassword(e.target.value)}
                            placeholder="At least 6 characters"
                            data-testid="hero-password-input"
                            className="w-full h-12 rounded-full bg-white text-slate-900 placeholder:text-slate-400 pl-6 pr-12 text-sm font-medium outline-none focus:ring-2 focus:ring-[#E11D48]/60"
                          />
                          <button
                            type="button"
                            onClick={() => setHeroShowPw((s) => !s)}
                            data-testid="hero-password-toggle"
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {heroShowPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="opacity-75">
                        <label className="block text-[11px] font-semibold text-white/60 mb-1.5 ml-1">Phone number — optional, you can skip this</label>
                        <div className="flex items-center h-10 rounded-full bg-white/55 px-2 focus-within:bg-white focus-within:ring-2 focus-within:ring-[#E11D48]/40 transition-colors">
                          <select
                            value={heroCc}
                            onChange={(e) => setHeroCc(e.target.value)}
                            data-testid="hero-cc-select"
                            className="h-full bg-transparent text-slate-700 text-xs font-bold pl-2 pr-1 outline-none cursor-pointer"
                          >
                            {HERO_CC.map(([flag, iso, code]) => (
                              <option key={iso + code} value={code}>{iso} {code}</option>
                            ))}
                          </select>
                          <span className="h-5 w-px bg-slate-300 mx-1.5" />
                          <input
                            type="tel"
                            value={heroPhone}
                            onChange={(e) => setHeroPhone(e.target.value.replace(/[^\d\s-]/g, ""))}
                            placeholder="651 347 2708"
                            data-testid="hero-phone-input"
                            className="flex-1 min-w-0 h-full bg-transparent text-slate-700 placeholder:text-slate-400 pr-4 text-sm font-medium outline-none"
                          />
                        </div>
                        <p className="text-[11px] text-white/60 mt-1.5 ml-1">Tip: for the most reliable connection, add your number — we'll ring you back so call quality never depends on your internet.</p>
                      </div>
                    </div>
                  )}
                  {heroError && (
                    <p className="text-sm font-semibold text-amber-300" data-testid="hero-error">{heroError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={heroLoading}
                    data-testid="hero-get-started-btn"
                    className="w-full h-12 rounded-full bg-[#E11D48] hover:bg-[#BE123C] text-white font-bold text-sm inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 shadow-lg shadow-red-900/30 disabled:opacity-60"
                  >
                    {heroLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {heroStep === 1 ? "Get started" : "Create my account"} <ArrowRight className="h-4 w-4" />
                  </button>
                </form>
                <p className="mt-6 text-sm text-white font-semibold">
                  Already have an account?{" "}
                  <Link to="/login" data-testid="hero-login-link" className="underline underline-offset-4 decoration-2 hover:text-white/80">
                    Log in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 -mt-2 md:-mt-6">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-xl px-6 py-5 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4">
            {[
              { icon: ShieldCheck, color: "text-[#2563EB]", title: "Secure & Reliable", desc: "Your calls are safe and private" },
              { icon: Headphones, color: "text-[#10B981]", title: "Crystal Clear Quality", desc: "Enjoy clear calls every time" },
              { icon: Clock, color: "text-[#8B5CF6]", title: "24/7 Support", desc: "We're here to help anytime" },
              { icon: Users, color: "text-[#F59E0B]", title: "Trusted by Thousands", desc: "Join callers who stay connected" },
            ].map((t, i) => (
              <div key={t.title} className={`flex items-start gap-3 ${i > 0 ? "lg:border-l lg:border-slate-100 lg:pl-6" : ""}`}>
                <t.icon className={`h-6 w-6 ${t.color} shrink-0 mt-0.5`} />
                <div>
                  <div className="text-sm font-bold text-slate-900">{t.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-center text-[#0F172A]">
          How it works
        </h2>
        <p className="text-center text-slate-500 mt-3 max-w-2xl mx-auto">
          You only need internet for a moment — to start the call. The conversation itself travels over the
          normal phone network, reaching any mobile or landline worldwide — <span className="font-semibold text-slate-700">even on 2G, no internet needed on their side</span>.
        </p>

        {/* 3-step how-to slideshow */}
        <div className="mt-10">
          <HowToSlideshow />
        </div>

        {/* Promo video */}
        <div className="mt-10 max-w-3xl mx-auto" data-testid="promo-video-section">
          <div className="rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/15 border border-slate-200 bg-black">
            <video
              controls
              preload="metadata"
              playsInline
              poster="/images/hero-woman-calling.png"
              className="w-full aspect-video"
              data-testid="promo-video"
            >
              <source src="/videos/zanitel_promo_wide.mp4" type="video/mp4" />
              Your browser does not support video playback.
            </video>
          </div>
          <p className="text-center text-xs text-slate-400 mt-3">▶ Watch: ZEEPTEL in 26 seconds</p>
        </div>

        {/* Step flow diagram */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-7 items-center gap-2" data-testid="how-it-works-diagram">
          {[
            { icon: CreditCard, color: "bg-[#2563EB]", title: "Top up your wallet", desc: "Pay in your own currency — card, worldwide" },
            { icon: Hash, color: "bg-[#10B981]", title: "Dial any number", desc: "See the exact rate and their local time first" },
            { icon: Smartphone, color: "bg-[#8B5CF6]", title: "Your phone rings", desc: "Answer it — the call runs on the phone network" },
            { icon: PhoneCall, color: "bg-[#E11D48]", title: "You're connected", desc: "Reach any mobile or landline, even on 2G" },
          ].map((s, i) => (
            <React.Fragment key={s.title}>
              <div className="md:col-span-1 flex flex-col items-center text-center bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-all h-full">
                <div className={`h-12 w-12 rounded-full ${s.color} flex items-center justify-center mb-3 shadow-lg`}>
                  <s.icon className="h-5 w-5 text-white" />
                </div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Step {i + 1}</div>
                <div className="font-heading font-bold text-sm">{s.title}</div>
                <p className="text-xs text-slate-500 mt-1.5">{s.desc}</p>
              </div>
              {i < 3 && (
                <div className="hidden md:flex items-center justify-center text-slate-300">
                  <ArrowRight className="h-6 w-6" />
                </div>
              )}
              {i < 3 && (
                <div className="md:hidden flex items-center justify-center text-slate-300 py-1">
                  <ArrowRight className="h-5 w-5 rotate-90" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Bridge diagram */}
        <div className="mt-10 bg-[#0F172A] rounded-3xl p-8 sm:p-10 text-white overflow-hidden relative" data-testid="bridge-diagram">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#3B82F6_1px,transparent_1px)] [background-size:22px_22px]" />
          <div className="relative">
            <div className="text-center text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-8">
              The ZEEPTEL bridge — why it works without internet
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6 max-w-3xl mx-auto">
              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                  <Smartphone className="h-7 w-7 text-blue-300" />
                </div>
                <div className="mt-3 font-bold text-sm">Your phone</div>
                <div className="text-xs text-slate-400">Internet needed only to start the call</div>
              </div>

              <div className="flex-1 flex items-center w-full sm:w-auto">
                <div className="flex-1 border-t-2 border-dashed border-blue-400/50 hidden sm:block" />
                <div className="mx-auto sm:mx-4 flex flex-col items-center">
                  <div className="h-14 w-14 rounded-full bg-[#2563EB] flex items-center justify-center shadow-lg shadow-blue-500/40">
                    <PhoneCall className="h-6 w-6 text-white" />
                  </div>
                  <div className="mt-2 text-xs font-bold text-blue-200">ZEEPTEL network</div>
                  <div className="text-[10px] text-slate-400">carries the call for you</div>
                </div>
                <div className="flex-1 border-t-2 border-dashed border-blue-400/50 hidden sm:block" />
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="h-16 w-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                  <Phone className="h-7 w-7 text-emerald-300" />
                </div>
                <div className="mt-3 font-bold text-sm">Their phone</div>
                <div className="text-xs text-slate-400">Mobile or landline — no internet, even 2G</div>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs">
              {["Caller needs internet only to start the call", "No app or internet for them", "Works on 2G & landlines", "Pay only for talk time"].map((t) => (
                <span key={t} className="px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-blue-100 font-medium">
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="esim" className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 text-blue-200 text-xs font-bold uppercase tracking-wide mb-6">
              <Plane className="h-3.5 w-3.5" /> New — Travel eSIMs
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
              Flying home? Land with <span className="text-[#60A5FA]">data already working.</span>
            </h2>
            <p className="mt-4 text-slate-300 max-w-lg">
              Buy an instant data eSIM for Uganda, Kenya, Nigeria, UAE and 16+ destinations before your trip.
              No airport SIM queues, no registration hassle — scan a QR code and you're connected when you land.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                { icon: QrCode, text: "Delivered instantly as a QR code — install in 1 minute" },
                { icon: Wallet, text: "Pay from your ZEEPTEL wallet, in any of 22 currencies" },
                { icon: Signal, text: "Keep your home SIM active — the eSIM handles your data" },
              ].map((f) => (
                <li key={f.text} className="flex items-center gap-3">
                  <span className="h-8 w-8 rounded-full bg-[#2563EB] flex items-center justify-center shrink-0">
                    <f.icon className="h-4 w-4 text-white" />
                  </span>
                  <span className="text-slate-200">{f.text}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/register"
              data-testid="esim-cta"
              className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-2xl bg-[#2563EB] text-white font-semibold hover:bg-[#1D4ED8] transition-all shadow-lg shadow-blue-500/25 hover:-translate-y-0.5"
            >
              <Smartphone className="h-4 w-4" /> Browse eSIM plans
            </Link>
          </div>

          <div data-testid="esim-country-marquee" className="min-w-0 overflow-hidden">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4 text-center lg:text-left">
              16+ destinations & growing — you're covered
            </div>
            {(() => {
              const ALL = [
                { flag: "🇺🇬", country: "Uganda", from: "$3.65" },
                { flag: "🇳🇬", country: "Nigeria", from: "$2.99" },
                { flag: "🇰🇪", country: "Kenya", from: "$3.49" },
                { flag: "🇦🇪", country: "UAE / Dubai", from: "$3.99" },
                { flag: "🇬🇭", country: "Ghana" },
                { flag: "🇹🇿", country: "Tanzania" },
                { flag: "🇪🇹", country: "Ethiopia" },
                { flag: "🇿🇦", country: "South Africa" },
                { flag: "🇪🇬", country: "Egypt" },
                { flag: "🇨🇲", country: "Cameroon" },
                { flag: "🇸🇳", country: "Senegal" },
                { flag: "🇺🇸", country: "United States" },
                { flag: "🇬🇧", country: "United Kingdom" },
                { flag: "🇨🇦", country: "Canada" },
                { flag: "🇫🇷", country: "France" },
                { flag: "🇮🇳", country: "India" },
              ];
              const rowA = ALL.slice(0, 8);
              const rowB = ALL.slice(8);
              const Card = ({ c }) => (
                <div className="shrink-0 w-40 bg-white/5 border border-white/10 rounded-2xl p-4 backdrop-blur hover:bg-white/10 transition-colors mx-2">
                  <div className="text-3xl">{c.flag}</div>
                  <div className="font-heading font-bold mt-1.5 text-sm truncate">{c.country}</div>
                  {c.from ? (
                    <>
                      <div className="text-[10px] text-slate-400 mt-0.5">Data plans from</div>
                      <div className="font-mono font-bold text-lg text-[#60A5FA]">{c.from}</div>
                    </>
                  ) : (
                    <div className="text-[11px] text-emerald-300 mt-1.5 font-semibold">Instant QR eSIM ✓</div>
                  )}
                </div>
              );
              return (
                <div className="space-y-4">
                  <div className="marquee-row overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]">
                    <div className="marquee-left flex w-max">
                      {[...rowA, ...rowA].map((c, i) => <Card key={`a-${c.country}-${i}`} c={c} />)}
                    </div>
                  </div>
                  <div className="marquee-row overflow-hidden [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]">
                    <div className="marquee-right flex w-max">
                      {[...rowB, ...rowB].map((c, i) => <Card key={`b-${c.country}-${i}`} c={c} />)}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </section>

      <section id="features" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-3 gap-6">
          {[
            { icon: Wallet, title: "Prepaid wallet", desc: "Top up securely and watch your balance update live as you call." },
            { icon: Globe, title: "Worldwide rates", desc: "Search any destination and see the exact per-minute price before you dial." },
            { icon: ShieldCheck, title: "Transparent billing", desc: "Per-minute deduction with full call history, duration and cost receipts." },
          ].map((f) => (
            <div key={f.title} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm hover:shadow-md transition-all duration-300">
              <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-[#2563EB]" />
              </div>
              <h3 className="font-heading font-bold text-lg">{f.title}</h3>
              <p className="text-slate-600 text-sm mt-2">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer id="about" className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center p-1.5">
              <img src="/branding/zanitel_mark.png" alt="Zeeptel logo" className="h-full w-auto" />
            </div>
            <div className="leading-tight">
              <span className="font-heading font-bold block">© 2026 Zanitel LLC · zeeptel.com</span>
              <span className="text-blue-200 text-xs uppercase tracking-[0.18em]">connecting the world</span>
            </div>
          </div>
          <div className="flex flex-col items-center sm:items-end gap-1.5">
            <span className="text-blue-200">Let&rsquo;s bridge the communication · Let us build the world</span>
            <div className="flex items-center gap-3 text-xs text-blue-300">
              <Link to="/about" data-testid="footer-about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" data-testid="footer-contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" data-testid="footer-terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>

      <SupportChat />
      <FeedbackCampaign />
    </div>
  );
}
